// Single finding drill-down — read details + triage (ack / suppress
// / fixed) + handover to /security/new with sha256 prefilled.

import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, ScanLine } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { CopyIconButton } from '../../components/ui/CopyIconButton';
import {
  patchFinding,
  useFinding,
  useFindingDiff,
  useFindingEvents,
  type FindingDiffEntry,
  type FindingStatus,
  type FindingSeverity,
} from '../../api/detection';

function severityTone(s: FindingSeverity): 'danger' | 'warning' | 'accent' | 'neutral' {
  if (s === 'critical' || s === 'high') return 'danger';
  if (s === 'medium') return 'warning';
  if (s === 'low') return 'accent';
  return 'neutral';
}

function eventKindTone(kind: string): 'success' | 'neutral' | 'warning' | 'accent' | 'danger' {
  if (kind === 'fixed') return 'success';
  if (kind === 'suppressed') return 'neutral';
  if (kind === 'acknowledged') return 'warning';
  if (kind === 'auto-reopened') return 'accent';
  return 'danger';
}

function diffToneTextClass(tone: 'danger' | 'success' | 'neutral'): string {
  if (tone === 'danger') return 'text-danger';
  if (tone === 'success') return 'text-success';
  return 'text-fg-muted';
}

function diffSectionColour(tone: 'danger' | 'success' | 'neutral'): string {
  if (tone === 'danger') return 'text-danger border-danger/30 bg-danger/5';
  if (tone === 'success') return 'text-success border-success/30 bg-success/5';
  return 'text-fg-muted border-border bg-bg-inset/40';
}

export function DetectionFindingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: finding, mutate, isLoading } = useFinding(id);
  const { data: events, mutate: mutateEvents } = useFindingEvents(id);
  const { data: diff } = useFindingDiff(id);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState(''); // datetime-local
  const [err, setErr] = useState<string | null>(null);

  const move = async (status: FindingStatus) => {
    if (!finding) return;
    setBusy(true);
    setErr(null);
    try {
      await patchFinding(finding.id, {
        status,
        suppress_reason: reason || undefined,
        suppressed_until: status === 'suppressed' && until
          ? new Date(until).toISOString()
          : undefined,
      });
      await mutate();
      await mutateEvents();
      setReason('');
      setUntil('');
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  // Handover into /security/new with sha256 + reason prefilled.
  const blockArtifact = () => {
    if (!finding) return;
    const params = new URLSearchParams({
      block_type: 'sha256',
      pattern: finding.sha256,
      reason: `Auto: ${finding.title} (${finding.rule_id})`,
      cve_id: finding.rule_id.startsWith('CVE-') || finding.rule_id.startsWith('GHSA-')
        ? finding.rule_id : '',
    });
    navigate(`/security/new?${params.toString()}`);
  };

  return (
    <AppShell>
      <div className="space-y-6">
        <Link to="/detection/findings" className="inline-flex items-center gap-2 text-fg-muted hover:text-fg text-sm">
          <ArrowLeft className="h-4 w-4" />
          Back to findings
        </Link>

        {isLoading && (
          <Card><CardBody><p className="text-fg-muted">Loading…</p></CardBody></Card>
        )}
        {!isLoading && !finding && (
          <Card><CardBody><p className="text-fg-muted">Finding not found.</p></CardBody></Card>
        )}
        {!isLoading && finding && (
          <>
            <div>
              <p className="orb-eyebrow text-accent/80 mb-2">Detection · finding</p>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
                  <ScanLine className="h-5 w-5 text-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge tone={severityTone(finding.severity)}>{finding.severity}</Badge>
                    <span className="orb-code text-xs">{finding.rule_id}</span>
                    <Badge tone={finding.status === 'open' ? 'danger' : 'neutral'}>{finding.status}</Badge>
                  </div>
                  <h1 className="text-2xl font-display font-semibold text-fg leading-tight mt-2">{finding.title}</h1>
                  {finding.detail && (
                    <p className="text-fg-muted text-sm mt-2 max-w-2xl">{finding.detail}</p>
                  )}
                </div>
              </div>
            </div>

            <Card>
              <CardHeader title="Where" />
              <CardBody className="space-y-3 text-sm">
                <Field label="Artifact path" value={finding.artifact_path} mono />
                <Field label="Repository" value={`${finding.project_slug} / ${finding.repo_slug}`} />
                <Field label="Format" value={finding.format} />
                <Field label="SHA-256" value={finding.sha256} mono />
                {finding.location && <Field label="Location" value={finding.location} mono />}
                <Field label="Scanner" value={finding.scanner_id} mono />
              </CardBody>
            </Card>

            {finding.fix_hint && (
              <Card>
                <CardHeader title="Fix hint" />
                <CardBody>
                  <p className="text-sm text-fg whitespace-pre-wrap">{finding.fix_hint}</p>
                </CardBody>
              </Card>
            )}

            {finding.evidence && Object.keys(finding.evidence).length > 0 && (
              <Card>
                <CardHeader title="Evidence" />
                <CardBody>
                  <pre className="text-xs font-mono text-fg overflow-x-auto bg-bg-inset/40 p-3 rounded">
                    {JSON.stringify(finding.evidence, null, 2)}
                  </pre>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardHeader title="Triage" description="Move the finding through the workflow. Acknowledge if it's known and accepted; suppress with reason (and an optional TTL) if it's a false positive; mark fixed when the underlying issue is resolved." />
              <CardBody className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    placeholder="reason (required for suppress)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Input
                    type="datetime-local"
                    title="suppressed until (auto-reopen)"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                  />
                </div>
                <p className="text-xs text-fg-muted">
                  Leaving the date empty makes the suppression indefinite. The TTL
                  reaper checks expirations hourly and reopens findings whose deadline has passed.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" disabled={busy || finding.status === 'acknowledged'} onClick={() => move('acknowledged')}>
                    Acknowledge
                  </Button>
                  <Button variant="secondary" disabled={busy || finding.status === 'suppressed' || !reason} onClick={() => move('suppressed')}>
                    Suppress {until && '(with TTL)'}
                  </Button>
                  <Button variant="secondary" disabled={busy || finding.status === 'fixed'} onClick={() => move('fixed')}>
                    Mark fixed
                  </Button>
                  <Button variant="secondary" disabled={busy || finding.status === 'open'} onClick={() => move('open')}>
                    Reopen
                  </Button>
                </div>
                {err && <p className="text-danger font-mono text-sm">{err}</p>}
              </CardBody>
            </Card>

            {diff && (diff.added.length + diff.persisted.length + diff.resolved.length > 0) && (
              <Card>
                <CardHeader
                  title={
                    diff.previous_version
                      ? `Diff vs ${diff.previous_version}`
                      : 'No prior version — first publish'
                  }
                  description={
                    diff.previous_version
                      ? 'Comparison between this artifact version and the previous upload of the same path. Added findings are regressions; resolved are fixes.'
                      : 'No earlier version of this artifact path exists in the repository, so every finding is "new since first publish".'
                  }
                />
                <CardBody className="space-y-4">
                  {diff.added.length > 0 && (
                    <DiffSection title={`Added (${diff.added.length})`} tone="danger" entries={diff.added} />
                  )}
                  {diff.resolved.length > 0 && (
                    <DiffSection title={`Resolved (${diff.resolved.length})`} tone="success" entries={diff.resolved} />
                  )}
                  {diff.persisted.length > 0 && (
                    <DiffSection title={`Persisted (${diff.persisted.length})`} tone="neutral" entries={diff.persisted} />
                  )}
                </CardBody>
              </Card>
            )}

            {events && events.length > 0 && (
              <Card>
                <CardHeader title="History" description="Append-only audit log of every status transition." />
                <CardBody>
                  <ol className="space-y-2">
                    {events.map((ev) => (
                      <li
                        key={ev.id}
                        className="flex items-start gap-3 px-2 py-1.5 rounded bg-bg-inset/40"
                      >
                        <Badge tone={eventKindTone(ev.kind)}>{ev.kind}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-fg">
                            {ev.actor || 'system'}
                            {ev.reason && <span className="text-fg-muted"> — {ev.reason}</span>}
                          </p>
                          {ev.until && (
                            <p className="text-xs text-fg-muted">
                              suppression TTL: {new Date(ev.until).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-fg-subtle whitespace-nowrap">
                          {new Date(ev.occurred_at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ol>
                </CardBody>
              </Card>
            )}

            <Card className="border-danger/30 bg-danger/5">
              <CardHeader
                title="Block this artifact at the gateway"
                description="Detection found the issue. Security blocks act on it — every download of this SHA returns 403."
                actions={
                  <Button variant="primary" onClick={blockArtifact}>
                    <ShieldAlert className="h-4 w-4" />
                    Create block
                  </Button>
                }
              />
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Field({ label, value, mono }: Readonly<{ label: string; value: string; mono?: boolean }>) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3">
      <p className="orb-section-title text-fg-muted">{label}</p>
      <p className={mono ? 'font-mono text-xs text-fg break-all' : 'text-fg'}>{value}</p>
    </div>
  );
}

function DiffSection({
  title,
  tone,
  entries,
}: Readonly<{
  title: string;
  tone: 'danger' | 'success' | 'neutral';
  entries: FindingDiffEntry[];
}>) {
  const colour = diffSectionColour(tone);
  return (
    <div>
      <p className={`orb-section-title mb-1.5 ${diffToneTextClass(tone)}`}>{title}</p>
      <ul className={`rounded border ${colour} divide-y divide-border/40`}>
        {entries.map((e, i) => (
          <li key={`${e.scanner_id}|${e.rule_id}|${e.location ?? ''}|${i}`} className="px-3 py-2 flex items-center gap-2">
            <Badge tone={severityTone(e.severity)}>{e.severity}</Badge>
            <span className="font-mono text-xs text-accent shrink-0">{e.rule_id}</span>
            <span className="text-sm text-fg flex-1 truncate" title={e.title}>{e.title}</span>
            {e.location && (
              <span className="inline-flex items-center gap-1 max-w-[16rem]">
                <span className="text-xs text-fg-muted font-mono truncate" title={e.location}>
                  {e.location}
                </span>
                <CopyIconButton value={e.location} label="Copy location" />
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
